import moment from "moment";

class PDFInvoiceReport {
    constructor() {
        this.reportData = null;
    }

    async exportToPDF(data) {
        this.reportData = data;

        await this.waitForPdfMakeReady();

        const docDefinition = this.buildDocDefinition();
        //ฟังก์ชันปริ้นเก่า
        //   pdfMake.createPdf(docDefinition).open();

        //ฟังก์ชันปริ้นใหม่
        return await new Promise((resolve, reject) => {
            try {
                pdfMake
                    .createPdf(docDefinition)
                    .getBlob(resolve);
            } catch (e) {
                reject(e);
            }
        });

    }

    waitForPdfMakeReady() {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (typeof pdfMake !== "undefined" && pdfMake.fonts) {
                    clearInterval(interval);
                    resolve(true);
                }
            }, 100);
        });
    }

    formatDate(date) {
        return moment(date).format("DD/MM/YYYY");
    }

    formatNumber(value) {
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value || 0);
    }

    // ================= HEADER =================
    buildHeader() {
        const header = this.reportData?.header || {};

        return [
            {
                columns: [
                    {
                        width: "55%",
                        stack: [
                            {
                                text: header.company_name_en || " ",
                                fontSize: 14,
                                bold: true,
                            },
                            {
                                text: header.company_name_th || " ",
                                fontSize: 11,
                                margin: [0, 2, 0, 0],
                            },
                        ],
                    },
                    {
                        width: "45%",
                        stack: [
                            {
                                text: "CONFIDENTIAL",
                                color: "red",
                                bold: true,
                                alignment: "right",
                                fontSize: 10,
                            },
                            {
                                text: "INVOICE REPORT", // 🔥 เปลี่ยนชื่อ
                                bold: true,
                                alignment: "right",
                                fontSize: 14,
                                margin: [0, 3, 0, 0],
                            },
                            {
                                text: `${this.formatDate(header.date_from)} - ${this.formatDate(header.date_to)}`,
                                alignment: "right",
                                fontSize: 9,
                                margin: [0, 2, 0, 0],
                            },
                        ],
                    },
                ],
                margin: [0, 0, 0, 10],
            },
        ];
    }

    // ================= STATUS / TOTAL HELPERS =================
    // Valid  = สถานะ Invoice เท่านั้น
    // Voided = สถานะ Void เท่านั้น
    isValidInvoice(item) {
        const s = String(item.status || "").trim().toLowerCase();
        return Number(item.status_code) === 3 || s === "invoice";
    }

    isVoidInvoice(item) {
        const s = String(item.status || "").trim().toLowerCase();
        return Number(item.status_code) === 4 || s === "void";
    }

    currencyOf(item) {
        return String(item.currency || "THB").trim().toUpperCase();
    }

    // THB, USD มาก่อนเสมอ แล้วตามด้วยสกุลอื่นที่มีในข้อมูล
    sortCurrencies(list) {
        const order = ["THB", "USD"];
        const rank = (c) => (order.indexOf(c) === -1 ? 99 : order.indexOf(c));
        return [...new Set([...order, ...list])].sort(
            (a, b) => rank(a) - rank(b) || a.localeCompare(b)
        );
    }

    // สรุปยอดตามสกุลเงิน: valid / voided / total (= valid + voided)
    calcTotals(items) {
        const map = {};
        items.forEach((item) => {
            const cur = this.currencyOf(item);
            if (!map[cur]) map[cur] = { valid: 0, voided: 0, total: 0 };
            const amt = Number(item.amount || 0);
            if (this.isValidInvoice(item)) map[cur].valid += amt;
            if (this.isVoidInvoice(item)) map[cur].voided += amt;
            // Total Amount = Valid Invoice Amount + Voided Amount
            map[cur].total = map[cur].valid + map[cur].voided;
        });
        this.sortCurrencies(Object.keys(map)).forEach((cur) => {
            if (!map[cur]) map[cur] = { valid: 0, voided: 0, total: 0 };
        });
        return map;
    }

    itemsOfSection(section) {
        const items = [];
        (section.dates || []).forEach((d) => items.push(...(d.items || [])));
        return items;
    }

    // ================= SUMMARY =================
    calcSummary() {
        const content = this.reportData?.content || [];
        const items = [];
        content.forEach((section) => items.push(...this.itemsOfSection(section)));

        return {
            total: items.length,
            valid: items.filter((i) => this.isValidInvoice(i)).length,
            voided: items.filter((i) => this.isVoidInvoice(i)).length,
            totals: this.calcTotals(items),
        };
    }

    buildSummary() {
        const sum = this.calcSummary();

        const countTile = (label, value) => ({
            stack: [
                { text: label, fontSize: 8.5, color: "#333333" },
                { text: String(value), fontSize: 15, bold: true, margin: [0, 4, 0, 0] },
            ],
            margin: [14, 2, 6, 2],
        });

        const amountTile = (label, value) => ({
            stack: [
                { text: label, fontSize: 8.5, color: "#333333", alignment: "center" },
                {
                    text: this.formatNumber(value),
                    fontSize: 16,
                    bold: true,
                    alignment: "center",
                    margin: [0, 4, 0, 0],
                },
            ],
            margin: [6, 2, 6, 2],
        });

        const currencies = this.sortCurrencies(Object.keys(sum.totals));
        const cells = [
            countTile("Total Invoices", sum.total),
            countTile("Valid Invoices", sum.valid),
            countTile("Voided Invoices", sum.voided),
            ...currencies.map((cur) =>
                amountTile(`Total Amount (${cur})`, sum.totals[cur].valid)
            ),
        ];
        const widths = [88, 88, 92, ...currencies.map(() => "*")];

        return [
            { text: "SUMMARY", bold: true, fontSize: 10.5, margin: [0, 10, 0, 6] },
            {
                table: { widths, body: [cells] },
                layout: {
                    hLineWidth: () => 0,
                    vLineWidth: (i, node) =>
                        i === 0 || i === node.table.widths.length ? 0 : 0.8,
                    vLineColor: () => "#BBBBBB",
                    paddingLeft: () => 0,
                    paddingRight: () => 0,
                    paddingTop: () => 0,
                    paddingBottom: () => 0,
                },
                margin: [0, 0, 0, 18],
            },
        ];
    }

    // ================= DEPARTMENT TOTAL =================
    buildSectionTotal(section) {
        const totals = this.calcTotals(this.itemsOfSection(section));
        const currencies = this.sortCurrencies(Object.keys(totals));

        const th = (text, alignment = "right") => ({
            text,
            bold: true,
            fontSize: 7.5,
            alignment,
            margin: [0, 2, 0, 3],
        });
        const td = (text, alignment = "right") => ({
            text,
            fontSize: 9,
            alignment,
            margin: [0, 3, 0, 3],
        });

        const body = [
            [
                th("CURRENCY", "left"),
                th("VALID INVOICE AMOUNT"),
                th("VOIDED AMOUNT"),
                th("TOTAL AMOUNT"),
            ],
            ...currencies.map((cur) => [
                td(cur, "left"),
                td(this.formatNumber(totals[cur].valid)),
                td(this.formatNumber(totals[cur].voided)),
                td(this.formatNumber(totals[cur].total)),
            ]),
        ];

        return {
            unbreakable: true,
            margin: [0, 4, 0, 18],
            stack: [
                {
                    canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.2 }],
                },
                {
                    margin: [0, 6, 0, 0],
                    columns: [
                        {
                            width: 160,
                            text: `TOTAL - ${section.department_name}`,
                            bold: true,
                            fontSize: 11,
                            margin: [4, 4, 0, 0],
                        },
                        {
                            width: "*",
                            table: { widths: [50, "*", 78, 78], body },
                            layout: {
                                hLineWidth: (i, node) =>
                                    i === 0 ? 0 : i === 1 || i === node.table.body.length ? 0.8 : 0.5,
                                hLineColor: (i) => (i === 1 ? "#333333" : "#BBBBBB"),
                                vLineWidth: () => 0,
                                paddingLeft: () => 4,
                                paddingRight: () => 4,
                            },
                        },
                    ],
                },
            ],
        };
    }

    // ================= CONTENT =================
    buildContent() {
        const content = this.reportData?.content || [];
        const result = [];

        const head = (text, alignment = "left") => ({
            text,
            bold: true,
            fontSize: 10,
            alignment,
            margin: [0, 2, 0, 4],
        });

        content.forEach((section) => {
            const body = [];

            // ===== HEADER ROW =====
            body.push([
                { ...head(section.department_name), fontSize: 11.5 },
                head("INVOICE NO."),
                head("AMOUNT", "right"),
                head("CUR", "center"),
                head("STATUS", "center"),
            ]);

            // ===== DATA =====
            section.dates.forEach((dateGroup, di) => {
                // DATE
                body.push([
                    {
                        text: this.formatDate(dateGroup.date),
                        bold: true,
                        fontSize: 10.5,
                        colSpan: 5,
                        margin: [0, di === 0 ? 8 : 12, 0, 2],
                    },
                    {}, {}, {}, {},
                ]);

                // ITEMS
                dateGroup.items.forEach((item) => {
                    const cell = (text, alignment = "left", margin = [0, 2, 0, 2]) => ({
                        text,
                        alignment,
                        margin,
                    });
                    body.push([
                        cell(item.client_name || ""),
                        cell(item.invoice_no || "####"),
                        cell(this.formatNumber(item.amount), "right", [0, 2, 8, 2]),
                        cell(item.currency || "", "center"),
                        cell(item.status || "", "center"),
                    ]);
                });
            });

            // ===== PUSH TABLE =====
            result.push({
                table: {
                    headerRows: 1,
                    widths: ["*", 95, 80, 45, 60],
                    body: body,
                },
                layout: {
                    hLineWidth: (i) => (i === 1 ? 1.2 : 0),
                    vLineWidth: () => 0,
                    paddingLeft: () => 4,
                    paddingRight: () => 4,
                    paddingTop: () => 0,
                    paddingBottom: () => 0,
                },
                margin: [0, 0, 0, 10],
            });

            // ===== TOTAL ของแผนก =====
            result.push(this.buildSectionTotal(section));
        });

        return result;
    }

    // ================= DOC =================
    buildDocDefinition() {
        return {
            pageSize: "A4",
            pageMargins: [40, 40, 40, 40],

            content: [...this.buildHeader(), ...this.buildSummary(), ...this.buildContent()],

            defaultStyle: {
                font: "Sarabun",
                fontSize: 10,
            },
        };
    }
}

export default new PDFInvoiceReport();